import {AsCastDirective} from 'as-cast';
import {NgxJsonViewerModule} from 'ngx-json-viewer';
import {ChipsModule} from 'primeng/chips';
import {DropdownModule} from 'primeng/dropdown';
import {FloatLabelModule} from 'primeng/floatlabel';
import {OverlayPanel, OverlayPanelModule} from 'primeng/overlaypanel';
import {SkeletonModule} from 'primeng/skeleton';
import {TooltipModule} from 'primeng/tooltip';

import {CdkCopyToClipboard} from '@angular/cdk/clipboard';
import {CdkDrag, CdkDragDrop, CdkDragHandle, CdkDropList} from '@angular/cdk/drag-drop';
import {JsonPipe, NgClass} from '@angular/common';
import {
	ChangeDetectionStrategy,
	ChangeDetectorRef,
	Component,
	computed,
	effect,
	inject,
	input,
	InputSignal,
	model,
	ModelSignal,
	OnInit,
	output,
	OutputEmitterRef,
	Signal,
	signal,
	WritableSignal,
} from '@angular/core';
import {FormsModule, ReactiveFormsModule} from '@angular/forms';
import {MatAutocompleteModule} from '@angular/material/autocomplete';
import {MatButton, MatIconButton} from '@angular/material/button';
import {MatButtonToggle, MatButtonToggleGroup} from '@angular/material/button-toggle';
import {MatCard, MatCardActions, MatCardContent, MatCardHeader, MatCardTitle} from '@angular/material/card';
import {MatDivider} from '@angular/material/divider';
import {MatIcon} from '@angular/material/icon';
import {MatInputModule} from '@angular/material/input';
import {MatTooltip} from '@angular/material/tooltip';
import {TranslateModule, TranslateService} from '@ngx-translate/core';
import {FaIconComponent} from '@shared/icon';
import {ToastService} from '@utils';

import {
	ObjectBuildAvailablePlaceholder,
	ObjectBuildType,
	parseUniquePlaceholderTemplateString,
	UniquePlaceholderData,
	UniquePlaceholderDataSource,
	UniquePlaceholderTemplateString,
} from './data/shared.types';
import {emptyCustomDataObjectFactory, ObjectBuildPair, ObjectDataLabel} from './data/types';

type PlaceholderGroup = {
	label: string;
	value: UniquePlaceholderDataSource;
	icon: string;
	items: ObjectBuildAvailablePlaceholder[];
};

@Component({
	selector: 'dc-simple-object-builder',
	imports: [
		TooltipModule,
		MatCard,
		MatCardHeader,
		MatCardTitle,
		NgClass,
		MatCardContent,
		MatAutocompleteModule,
		ReactiveFormsModule,
		FormsModule,
		MatInputModule,
		JsonPipe,
		MatIcon,
		MatCardActions,
		MatButtonToggleGroup,
		MatButtonToggle,
		TranslateModule,
		MatTooltip,
		FaIconComponent,
		CdkDropList,
		CdkDrag,
		CdkDragHandle,
		DropdownModule,
		MatDivider,
		AsCastDirective,
		FloatLabelModule,
		ChipsModule,
		OverlayPanelModule,
		NgxJsonViewerModule,
		SkeletonModule,
		CdkCopyToClipboard,
		MatButton,
		MatIconButton,
	],
	templateUrl: './simple-object-builder.component.html',
	styleUrl: './simple-object-builder.component.scss',
	changeDetection: ChangeDetectionStrategy.OnPush,
})
export class SimpleObjectBuilderComponent implements OnInit {
	/* ------------------------------------------------  Inputs ------------------------------------------------ */
	public readonly object: ModelSignal<ObjectBuildType> = model.required<ObjectBuildType>();
	public readonly placeholders: InputSignal<ObjectBuildAvailablePlaceholder[]> = model.required<ObjectBuildAvailablePlaceholder[]>();
	public readonly title: InputSignal<string> = input<string>();
	public readonly componentDescription: InputSignal<string> = input<string>();
	public readonly selectPlaceholderPrompt: InputSignal<string> = input<string>('components.simple-object-builder.select-data-source');
	public readonly placeholderPatternBuilder: InputSignal<RegExp> = input<RegExp>(/<<(.*?)>>/g);
	/* ------------------------------------------------  Outputs ------------------------------------------------ */
	public readonly cancel: OutputEmitterRef<void> = output<void>();
	/* ------------------------------------------  Types and Constants ------------------------------------------ */
	protected readonly ObjectBuildAvailablePlaceholder!: ObjectBuildAvailablePlaceholder;
	protected readonly toastService: ToastService = inject(ToastService);
	protected readonly objectPairs$: WritableSignal<ObjectBuildPair[]> = signal<ObjectBuildPair[]>([]);
	protected readonly isObjectPairsValid$: Signal<boolean> = computed(() => {
		const objectPairs = this.objectPairs$();
		return objectPairs && objectPairs.length && objectPairs.every(pair => pair.selected_placeholder);
	});
	protected readonly isObjectValid$: Signal<boolean> = computed(() => {
		const objectValue = this.object();
		return objectValue && Object.keys(objectValue).length > 0;
	});
	protected readonly previewObject$: WritableSignal<ObjectBuildType> = signal<ObjectBuildType>({});
	protected readonly wrappedObjectWithData$: Signal<{data: ObjectBuildType}> = computed(() => {
		const objectValue = this.previewObject$();
		return {data: objectValue};
	});
	/* -------------------------------------------------- Data -------------------------------------------------- */
	protected readonly get_CUSTOM_DATA_OPTIONS: () => ObjectBuildAvailablePlaceholder = null;
	/* ------------------------------------------------  Signals ------------------------------------------------ */
	protected readonly placeholderOptions$: Signal<PlaceholderGroup[]> = computed(() => {
		const groups: PlaceholderGroup[] = [];

		// Custom data - first option
		const customData = this.get_CUSTOM_DATA_OPTIONS();
		groups.push({
			label: undefined,
			value: UniquePlaceholderDataSource.constant,
			icon: undefined,
			items: [customData],
		});

		const placeholders = this.placeholders() || [];
		// User info source
		const userInfoSource = placeholders.filter(placeholder => placeholder.source === UniquePlaceholderDataSource.user_info);
		groups.push({
			label: 'components.simple_object_builder.user_data',
			value: UniquePlaceholderDataSource.user_info,
			icon: 'badge',
			items: userInfoSource,
		});

		// Meta tags source
		const metaTagsSource = placeholders.filter(placeholder => placeholder.source === UniquePlaceholderDataSource.meta_tag);
		groups.push({
			label: 'components.simple_object_builder.tags',
			value: UniquePlaceholderDataSource.meta_tag,
			icon: 'tag',
			items: metaTagsSource,
		});

		return groups;
	});
	/* ------------------------------------------ PROVIDERS / SERVICES ------------------------------------------ */
	private readonly translateService: TranslateService = inject(TranslateService);
	private readonly placeholderOptionGroups$: Signal<Map<UniquePlaceholderDataSource, ObjectBuildAvailablePlaceholder[]>> = computed(() => {
		const map = new Map<UniquePlaceholderDataSource, ObjectBuildAvailablePlaceholder[]>();
		this.placeholderOptions$().forEach(group => {
			map.set(group.value, group.items);
		});
		return map;
	});

	/* ------------------------------------------ Constructor ------------------------------------------ */
	constructor(private cdr: ChangeDetectorRef) {
		this.get_CUSTOM_DATA_OPTIONS = emptyCustomDataObjectFactory(this.translateService);
		const objectEffectRef$ = effect(() => {
			const objectValue = this.object();
			if (!objectValue || Object.keys(objectValue).length === 0) {
				this.addEmptyPair();
			} else if (objectValue) {
				const allAvailablePlaceholderOptions = this.placeholderOptionGroups$();
				const objectPairs = new Array<ObjectBuildPair>();
				Object.entries(objectValue).forEach(([data_label, output_placeholder], index) => {
					const parsedPlaceholder: UniquePlaceholderData = parseUniquePlaceholderTemplateString(output_placeholder);
					let selected_placeholder: ObjectBuildAvailablePlaceholder = undefined;
					switch (parsedPlaceholder.source) {
						case UniquePlaceholderDataSource.constant:
							selected_placeholder = this.get_CUSTOM_DATA_OPTIONS();
							break;
						case UniquePlaceholderDataSource.user_info:
							selected_placeholder = allAvailablePlaceholderOptions.get(UniquePlaceholderDataSource.user_info).find(placeholder => placeholder.key === parsedPlaceholder.key);
							break;
						case UniquePlaceholderDataSource.meta_tag:
							selected_placeholder = allAvailablePlaceholderOptions.get(UniquePlaceholderDataSource.meta_tag).find(placeholder => placeholder.key === parsedPlaceholder.key);
							break;
					}
					if (selected_placeholder)
						objectPairs.push({
							index,
							data_label,
							selected_placeholder,
							output_placeholder,
							value: parsedPlaceholder.key,
						});
				});
				this.objectPairs$.set(objectPairs);
			}

			cdr.detectChanges();
			objectEffectRef$.destroy();
		});
	}

	/* ------------------------------------------ Lifecycle Hooks ------------------------------------------ */
	ngOnInit(): void {}

	/* -------------------------------------------------- Methods -------------------------------------------------- */

	protected addEmptyPair(addPairButton?: MatButtonToggle) {
		if (addPairButton) addPairButton.checked = false;
		const newPair = this.dataPairFactory();
		this.objectPairs$.update(pairs => {
			pairs.push(newPair);
			return pairs;
		});
	}

	protected saveObject(previewOverlayPanel: OverlayPanel) {
		const buildedObject = this._buildObjectPairs();
		this.object.set(buildedObject);
		this.toastService.open({
			message: 'components.simple-object-builder.object-saved',
			type: 'success',
		});
	}

	protected onCancel() {
		this.object.set(undefined);
		this.cancel.emit();
	}

	protected showPreview(previewButton: MatButtonToggle, previewOverlayPanel: OverlayPanel, $event: MouseEvent) {
		previewButton.checked = false;
		const builtObject = this._buildObjectPairs();
		this.previewObject$.set(builtObject);
		previewOverlayPanel.show($event);
	}

	protected resetObjectPairs(resetButton: MatButtonToggle) {
		resetButton.checked = false;
		this.objectPairs$.set([]);
		this.addEmptyPair();
	}

	protected deletePair(pairIndex: number) {
		this.objectPairs$.update(pairs => {
			pairs.splice(pairIndex, 1);
			return pairs;
		});
		this.addEmptyPair();
	}

	protected patchPair(pair: ObjectBuildPair, patch: Partial<ObjectBuildPair>) {
		Object.assign(pair, patch);
		this.objectPairs$.update(pairs => {
			const index = pair.index;
			pairs[index] = pair;
			return [...pairs];
		});
		this.cdr.detectChanges();
	}

	protected reorderPairs($event: CdkDragDrop<any, any, any>) {
		const originalIndex = $event.previousIndex;
		const currentIndex = $event.currentIndex;

		this.objectPairs$.update(pairs => {
			// Swap the pairs (and	update the index)
			const temp = pairs[originalIndex];
			pairs[originalIndex] = pairs[currentIndex];
			pairs[currentIndex] = temp;
			pairs[originalIndex].index = originalIndex;
			pairs[currentIndex].index = currentIndex;
			return [...pairs];
		});
		this.cdr.detectChanges();
	}

	private _buildObjectPairs() {
		const newObject = {};
		this.objectPairs$().forEach(pair => {
			if (!pair.selected_placeholder) return;
			const output_placeholder = this.getPlaceholderLabel(pair);
			newObject[pair.data_label] = output_placeholder;
		});
		return newObject;
	}

	private dataPairFactory(data_label: ObjectDataLabel = '', selected_placeholder: ObjectBuildAvailablePlaceholder = undefined): ObjectBuildPair {
		const pair: ObjectBuildPair = {
			index: this.objectPairs$().length,
			data_label,
			selected_placeholder,
			output_placeholder: undefined,
		};
		pair.output_placeholder = this.getPlaceholderLabel(pair) as UniquePlaceholderTemplateString<UniquePlaceholderDataSource, string>;
		return pair;
	}

	private getPlaceholderLabel<P extends ObjectBuildPair = ObjectBuildPair>(pair: P): UniquePlaceholderTemplateString<UniquePlaceholderDataSource, string> {
		if (!pair.selected_placeholder) return undefined;
		if (pair.selected_placeholder.source === UniquePlaceholderDataSource.constant && pair.value) {
			return `<<${pair.selected_placeholder.source}::${pair.data_label}::${pair.value}>>` as UniquePlaceholderTemplateString<UniquePlaceholderDataSource, string>;
		}
		return `<<${pair.selected_placeholder.source}::${pair.selected_placeholder.key}>>` as UniquePlaceholderTemplateString<UniquePlaceholderDataSource, string>;
	}
}
